import axios from "axios";
import createAuthRefresh from "axios-auth-refresh";
import auth from "../shared/Auth0";

const ACCESS_TOKEN_KEY = "access_token";
const AUTHORIZATION_HEADER_KEY = "Authorization";

// Read fresh token from localStorage on every request.
// Token may have been updated by scheduleRenewal, visibilityChange handler, or 401 recovery.
axios.interceptors.request.use((config) => {
  // Guard: Axios v1 always provides an AxiosHeaders instance (with set/delete) but be defensive for edge-case custom configs
  config.headers = config.headers ?? {};
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const supportsHeaderMethods =
    typeof config.headers.set === "function" &&
    typeof config.headers.delete === "function";
  if (supportsHeaderMethods) {
    if (token) {
      config.headers.set(AUTHORIZATION_HEADER_KEY, `Bearer ${token}`);
    } else {
      config.headers.delete(AUTHORIZATION_HEADER_KEY);
    }
  } else if (token) {
    config.headers[AUTHORIZATION_HEADER_KEY] = `Bearer ${token}`;
  } else {
    delete config.headers[AUTHORIZATION_HEADER_KEY];
  }
  return config;
});

// On 401: attempt one silent refresh via Auth0 checkSession, then retry all queued requests.
// createAuthRefresh queues concurrent axios 401s; refreshSession also shares the in-flight
// checkSession with the renewal timer and the fetch-based ApiClient (authFetch).
// Transient refresh failures only fail the queued requests; the renewal timer retries.
const refreshAuthLogic = () =>
  auth.refreshSession().catch((err) => {
    if (!auth.isSessionLost(err)) {
      throw new Error("Token refresh failed");
    }
    window.location.href = `${window.location.origin}/logout`;
    throw new Error("Token refresh failed — redirecting to logout");
  });

createAuthRefresh(axios, refreshAuthLogic);

// Normalize non-401 error shape
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    return Promise.reject(error?.response ?? error);
  },
);

export default axios;
